import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const FAQ_SECTIONS = [
  {
    id: 'pricing',
    title: 'Motor de Precios',
    icon: 'Calculator',
    items: [
      { q: 'Como se calcula el precio maximo de compra?', a: 'El precio maximo de compra (max_buy) se obtiene restando al valor recuperable los costes de logistica, procesamiento, el margen objetivo y el buffer de riesgo. Formula: max_buy = rendimiento x precio_esperado - logistica - procesamiento - margen - buffer_riesgo.' },
      { q: 'Que es el rendimiento (yield)?', a: 'El rendimiento es el porcentaje del peso bruto del lote que se convierte en metal vendible tras el procesamiento. Por ejemplo, un yield del 92% significa que de 1.000 kg brutos obtienes 920 kg netos facturables.' },
      { q: '¿Qué es el buffer de riesgo?', a: 'El buffer de riesgo protege frente a la posible caída del precio del metal durante el tiempo que el lote permanece en inventario. En modo dinámico se calcula usando volatilidad histórica: σ_diaria × √días × 1.65 × benchmark_EUR × yield. El factor 1.65 corresponde al nivel de confianza del 95% (VaR paramétrico). En modo manual el usuario introduce directamente el porcentaje deseado.' },
      { q: 'Como funcionan los escenarios de shock?', a: 'Los escenarios de shock simulan caidas de precio del mercado (p.ej. -5%, -10%, -15%) aplicadas al benchmark actual. Permiten ver cuanto perderia el lote si el mercado cayera antes de la venta, ayudando a fijar el buffer adecuado.' },
    ],
  },
  {
    id: 'market',
    title: 'Datos de Mercado',
    icon: 'TrendingUp',
    items: [
      { q: 'Que es el LME?', a: 'El LME (London Metal Exchange) es el mercado de referencia mundial para metales no ferrosos. Sus precios en USD/tonelada se usan como base para calcular el benchmark en euros: benchmark_EUR = LME_USD / EURUSD.' },
      { q: 'Como establezco un indice regional?', a: 'En la pantalla Datos de Mercado, seccion Entrada Manual, puedes introducir un precio regional especifico para un metal. Este precio sobreescribe el LME como referencia para ese metal en los calculos de la calculadora.' },
      { q: 'Como funciona la conversion EUR/USD?', a: 'El sistema divide el precio LME en USD entre el tipo de cambio EUR/USD para obtener el benchmark en euros. Si el LME del cobre es 9.200 USD/t y el EUR/USD es 1,08, el benchmark EUR es 9.200 / 1,08 = 8.519 EUR/t.' },
    ],
  },
  {
    id: 'inventory',
    title: 'Inventario y Riesgo',
    icon: 'Package',
    items: [
      { q: 'Como se calcula la exposicion?', a: 'La exposicion total en euros es la suma de (cantidad_t x yield x benchmark_EUR) para todos los lotes en inventario. Representa el valor de mercado teorico del inventario al precio actual.' },
      { q: 'Que activa una alerta de riesgo?', a: 'Las alertas se activan cuando la exposicion de un metal supera el umbral configurado en Configuracion > Umbrales de Riesgo, o cuando la perdida potencial bajo el escenario de shock supera el limite definido.' },
      { q: 'Que es el P&L latente?', a: 'El P&L latente (ganancia/perdida no realizada) es la diferencia entre el precio al que compraste el lote y el valor de mercado actual. Un P&L latente negativo indica que el mercado ha caido desde la compra.' },
    ],
  },
  {
    id: 'sales',
    title: 'Ventas y Calibracion',
    icon: 'BarChart2',
    items: [
      { q: 'Como funciona la calibracion de rendimiento?', a: 'La calibracion de yield compara el rendimiento teorico usado en la calculadora con el rendimiento real obtenido en la venta. Si el real es sistematicamente inferior, debes ajustar el yield por defecto en Configuracion para mejorar la precision de los precios futuros.' },
      { q: 'Que es la calibracion de descuento?', a: 'La calibracion de descuento compara el descuento esperado (configurado en la categoria) con el descuento real negociado en cada venta. Permite detectar si estas siendo demasiado optimista o conservador en tus estimaciones de precio de venta.' },
    ],
  },
  {
    id: 'settings',
    title: 'Configuracion',
    icon: 'Settings',
    items: [
      { q: 'Como anado una nueva categoria?', a: 'Ve a Configuracion > Catalogo de Categorias y pulsa Nueva Categoria. Introduce el nombre, el metal base, el yield por defecto y el descuento. La categoria quedara disponible inmediatamente en la calculadora.' },
      { q: 'Como configuro los parametros de volatilidad?', a: 'En Configuracion > Volatilidad y Shock puedes definir el porcentaje de caida para cada horizonte temporal (1d, 7d, 30d). Estos valores se usan para calcular el buffer de riesgo automaticamente segun los dias estimados de permanencia en inventario.' },
    ],
  },
];

function AccordionItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-b-0" style={{ borderColor: 'var(--color-border)' }}>
      <button
        className="w-full flex items-center justify-between gap-3 py-3.5 text-left transition-standard hover:text-accent"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-medium text-foreground" style={{ fontSize: '0.9rem', fontFamily: '"IBM Plex Sans", sans-serif' }}>
          {item?.q}
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} color="var(--color-muted-foreground)" strokeWidth={2} className="flex-shrink-0" />
      </button>
      {open && (
        <div className="pb-4 pr-6" style={{ fontSize: '0.875rem', fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-muted-foreground)', lineHeight: 1.65 }}>
          {item?.a}
        </div>
      )}
    </div>
  );
}

function FaqSection({ section }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
      <button
        className="w-full flex items-center gap-3 px-5 py-4 text-left transition-standard hover:bg-muted"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-md"
          style={{ width: 32, height: 32, background: 'color-mix(in srgb, var(--color-accent) 15%, transparent)' }}
        >
          <Icon name={section?.icon} size={16} color="var(--color-accent)" strokeWidth={2} />
        </div>
        <span className="flex-1 font-semibold text-foreground" style={{ fontSize: '0.9375rem', fontFamily: 'var(--font-heading)' }}>
          {section?.title}
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded-full mr-2"
          style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          {section?.items?.length} preguntas
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} color="var(--color-muted-foreground)" strokeWidth={2} />
      </button>
      {open && (
        <div className="px-5 pb-2" style={{ borderTop: '1px solid var(--color-border)' }}>
          {section?.items?.map((item, i) => (
            <AccordionItem key={i} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function UserGuideTab() {
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground" style={{ fontSize: '0.9rem', fontFamily: '"IBM Plex Sans", sans-serif', lineHeight: 1.6 }}>
        Preguntas frecuentes agrupadas por modulo. Haz clic en una seccion para expandirla.
      </p>
      {FAQ_SECTIONS?.map((section) => (
        <FaqSection key={section?.id} section={section} />
      ))}
    </div>
  );
}
